import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Construction } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Page, PageHeader } from "@/components/ui/page";
import type { TranslationKey } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";

/**
 * Every section that exists in the menu but not yet in the product. One route,
 * a slug each: the pages arrive by replacing an entry here with a real file.
 */
export const COMING_SOON = {
  maps: { title: "nav.maps", text: "page.mapsText" },
} as const satisfies Record<string, { title: TranslationKey; text: TranslationKey }>;

export type ComingSoonSlug = keyof typeof COMING_SOON;

export const isComingSoon = (slug: string): slug is ComingSoonSlug => slug in COMING_SOON;

function ComingSoonPage() {
  const { page } = useParams() as { page: string };
  const t = useTranslation();
  // The file route threw `notFound()` from its loader for a slug that is not a
  // menu entry. There is no loader to throw from now, so an unknown slug is
  // sent back to the dashboard rather than rendering an undefined entry.
  if (!isComingSoon(page)) return <Navigate to="/" replace />;
  const entry = COMING_SOON[page as ComingSoonSlug];
  const title = t(entry.title);

  return (
    <Page>
      <PageHeader title={title} description={t(entry.text)} />
      <Card>
        <EmptyState
          icon={Construction}
          title={t("common.comingSoon")}
          description={`${t("common.onItsWay").replace("{section}", title)} ${t("page.nothingYet")}`}
          action={
            <Button asChild variant="secondary">
              <Link to="/">
                <ArrowLeft aria-hidden /> {t("page.backToDashboard")}
              </Link>
            </Button>
          }
        />
      </Card>
    </Page>
  );
}

export default ComingSoonPage;
